/**
 * BetaLanding — public unauthenticated /beta route.
 *
 * Presentation-ready V2 landing page for pitching Informed OS
 * to prospective beta cohorts. All sections + copy come from the P0 brief;
 * no marketing loosening allowed at the token level (banned strings never
 * appear as visible copy). Contextual product-mode uses of the word "beta"
 * are permitted here per the guardrail exception.
 */
import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Briefcase,
  Check,
  Compass,
  CreditCard,
  FileEdit,
  Home,
  Loader2,
  ShieldCheck,
  Award,
  Target,
} from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { trackEvent } from "@/lib/analytics";

const APP_NAME = "Informed OS";

const CAPABILITIES = [
  {
    key: "credit",
    label: "Credit Readiness",
    icon: CreditCard,
    body: "Understand your credit profile, identify improvement opportunities, and prepare for funding.",
  },
  {
    key: "funding",
    label: "Funding Readiness",
    icon: Award,
    body: "Analyze your financial profile and discover your next best opportunities.",
  },
  {
    key: "mortgage",
    label: "Mortgage Readiness",
    icon: Home,
    body: "Evaluate your home-buying readiness and estimate your purchase power.",
  },
  {
    key: "opportunities",
    label: "Funding Opportunities",
    icon: Briefcase,
    body: "See what business funding and personal credit products you qualify for today.",
  },
  {
    key: "blueprint",
    label: "Financial Blueprint",
    icon: Compass,
    body: "Follow your personalized action plan toward stronger financial readiness.",
  },
  {
    key: "grants",
    label: "AI Proposal & Grant Tools",
    icon: FileEdit,
    body: "Discover matching grants and generate professional funding proposals. Available Soon.",
    isSoon: true,
  },
];

const AUDIENCE = [
  "Consumers improving their credit",
  "Entrepreneurs preparing for business funding",
  "Future homeowners",
  "Credit education clients",
  "Financial coaching clients",
  "Early users willing to provide feedback",
];

const BETA_FEATURES = [
  "Funding Readiness Score",
  "Credit Report Upload & Analysis",
  "AI Credit Readiness Review",
  "Mortgage Readiness Profile",
  "Funding Opportunity Matching",
  "Personalized Financial Blueprint",
  "Feedback Widget (share your thoughts directly from any page)",
];

const INTEREST_OPTIONS = [
  { value: "credit", label: "Credit" },
  { value: "funding", label: "Funding" },
  { value: "mortgage", label: "Mortgage" },
  { value: "grants", label: "Grants" },
  { value: "all", label: "All" },
];

const DISCLAIMER_TEXT =
  "Informed OS provides educational financial readiness tools. It does not guarantee funding approval, credit score increases, mortgage approval, or account removals. All recommendations are informational and should be reviewed before taking action.";

function isValidEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());
}
function isValidPhone(v) {
  // US-friendly: needs at least 7 digits after stripping non-numeric chars.
  const digits = String(v || "").replace(/\D+/g, "");
  return digits.length >= 7 && digits.length <= 15;
}

const EMPTY_FORM = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  interest_area: "",
  is_current_client: "",
  message: "",
};

export default function BetaLanding() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null); // { message, beta_code_instructions? }
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const formRef = useRef(null);

  const remaining = useMemo(() => 500 - (form.message || "").length, [form.message]);

  const setField = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    setFieldErrors((e) => (e[name] ? { ...e, [name]: null } : e));
  };

  const validate = () => {
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = "First name is required.";
    if (!form.last_name.trim()) errs.last_name = "Last name is required.";
    if (!isValidEmail(form.email)) errs.email = "Enter a valid email address.";
    if (!isValidPhone(form.phone)) errs.phone = "Enter a valid phone number.";
    if (!form.interest_area) errs.interest_area = "Choose an interest area.";
    if (form.is_current_client !== "yes" && form.is_current_client !== "no") {
      errs.is_current_client = "Please answer.";
    }
    if ((form.message || "").length > 500) {
      errs.message = "Message must be 500 characters or fewer.";
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    if (!validate()) return;

    setSubmitting(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        interest_area: form.interest_area,
        is_current_client: form.is_current_client === "yes",
        message: (form.message || "").trim() || null,
      };
      const { data } = await api.post("/beta/waitlist", payload);
      setConfirmation({
        message: data?.message || "Thanks! We've received your request.",
        beta_code_instructions: data?.beta_code_instructions || null,
      });
      // Best-effort analytics — never blocks submission.
      trackEvent("beta_access_requested", {
        interest_area: payload.interest_area,
        is_current_client: payload.is_current_client,
      });
    } catch (err) {
      // 429 rate limit gets a specifically-worded message.
      const isRateLimit = err?.response?.status === 429;
      const detail = err?.response?.data?.detail;
      setError(
        isRateLimit
          ? detail ||
            "You've submitted this form several times recently. Please wait a little while before trying again."
          : formatApiError(err) || "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    // Focus the first input for keyboard users.
    setTimeout(() => {
      formRef.current?.querySelector("input[name='first_name']")?.focus();
    }, 350);
  };

  return (
    <div
      data-testid="beta-landing-page"
      className="min-h-screen"
      style={{ background: "var(--v2-background)", color: "var(--v2-text)" }}
    >
      {/* Top bar — brand + Sign In */}
      <header
        data-testid="beta-topbar"
        className="sticky top-0 z-30 backdrop-blur-md border-b"
        style={{
          background: "color-mix(in srgb, var(--v2-background) 88%, transparent)",
          borderColor: "var(--v2-border)",
        }}
      >
        <div className="max-w-6xl mx-auto px-5 md:px-10 h-14 flex items-center justify-between">
          <Link
            to="/"
            data-testid="beta-brand-link"
            className="font-semibold text-[15px] tracking-tight"
            style={{ color: "var(--v2-text)" }}
          >
            {APP_NAME}
          </Link>
          <Link
            to="/login"
            data-testid="beta-topbar-signin"
            className="text-sm hover:underline"
            style={{ color: "var(--v2-text-muted)" }}
          >
            Sign In
          </Link>
        </div>
      </header>

      {/* Section 1 — Hero */}
      <section
        data-testid="beta-hero"
        className="relative overflow-hidden"
      >
        <div className="relative max-w-4xl mx-auto px-5 md:px-10 pt-16 md:pt-24 pb-14 md:pb-20">
          <div
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em]"
            style={{ color: "var(--v2-accent)" }}
            data-testid="beta-hero-eyebrow"
          >
            <Award className="w-3.5 h-3.5" /> Beta Access
          </div>
          <h1
            data-testid="beta-hero-headline"
            className="mt-3 font-semibold tracking-tight text-[30px] sm:text-[36px] md:text-[48px] leading-[1.08]"
          >
            Become One of the First to Experience Informed OS
          </h1>
          <p
            data-testid="beta-hero-subheadline"
            className="mt-4 text-base md:text-lg max-w-3xl leading-relaxed"
            style={{ color: "var(--v2-text-muted)" }}
          >
            An AI-powered financial readiness platform designed to help users understand their
            credit, prepare for funding, evaluate mortgage readiness, and follow a personalized
            financial blueprint.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={scrollToForm}
              data-testid="beta-hero-primary-cta"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-all hover:translate-y-[-1px]"
              style={{
                background: "var(--v2-accent)",
                color: "#fff",
                boxShadow: "0 10px 24px -8px color-mix(in srgb, var(--v2-accent) 55%, transparent)",
              }}
            >
              Request Beta Access <ArrowRight className="w-4 h-4" />
            </button>
            <Link
              to="/login"
              data-testid="beta-hero-secondary-cta"
              className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-medium border transition-colors"
              style={{
                borderColor: "var(--v2-border)",
                color: "var(--v2-text)",
                background: "var(--v2-surface)",
              }}
            >
              Sign In
            </Link>
          </div>
        </div>
      </section>

      {/* Section 2 — Capabilities */}
      <section
        data-testid="beta-capabilities"
        className="relative border-t"
        style={{ borderColor: "var(--v2-border)" }}
      >
        <div className="max-w-6xl mx-auto px-5 md:px-10 py-14 md:py-20">
          <div className="max-w-2xl">
            <div
              className="text-[10px] uppercase tracking-[0.22em]"
              style={{ color: "var(--v2-accent)" }}
            >
              What the Platform Helps With
            </div>
            <h2 className="mt-2 font-semibold tracking-tight text-[24px] md:text-[30px]">
              Six modules working together
            </h2>
            <p
              className="mt-2 text-sm md:text-base leading-relaxed"
              style={{ color: "var(--v2-text-muted)" }}
            >
              Each area of the platform contributes to a single funding readiness picture — no
              disconnected point tools.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {CAPABILITIES.map(({ key, label, icon: Icon, body, isSoon }) => (
              <div
                key={key}
                data-testid={`beta-capability-${key}`}
                className="rounded-2xl border p-5 md:p-6 h-full flex flex-col gap-3 transition-transform hover:translate-y-[-2px]"
                style={{
                  background: "var(--v2-surface)",
                  borderColor: "var(--v2-border)",
                }}
              >
                <div
                  className="inline-flex items-center justify-center w-10 h-10 rounded-xl"
                  style={{
                    background:
                      "color-mix(in srgb, var(--v2-accent) 14%, transparent)",
                    color: "var(--v2-accent)",
                  }}
                >
                  <Icon className="w-5 h-5" aria-hidden="true" />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-base md:text-lg font-semibold tracking-tight">
                    {label}
                  </h3>
                  {isSoon ? (
                    <span
                      data-testid={`beta-capability-${key}-soon`}
                      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] border"
                      style={{
                        borderColor: "var(--v2-border)",
                        color: "var(--v2-text-muted)",
                        background: "var(--v2-surface-subtle)",
                      }}
                    >
                      Available Soon
                    </span>
                  ) : null}
                </div>
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: "var(--v2-text-muted)" }}
                >
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3 — Who this beta is for */}
      <section
        data-testid="beta-audience"
        className="relative border-t"
        style={{ borderColor: "var(--v2-border)", background: "var(--v2-surface-subtle)" }}
      >
        <div className="max-w-4xl mx-auto px-5 md:px-10 py-14 md:py-20">
          <div
            className="text-[10px] uppercase tracking-[0.22em]"
            style={{ color: "var(--v2-accent)" }}
          >
            Audience
          </div>
          <h2 className="mt-2 font-semibold tracking-tight text-[24px] md:text-[30px]">
            Who this beta is for
          </h2>
          <p className="mt-2 text-sm md:text-base" style={{ color: "var(--v2-text-muted)" }}>
            This beta is ideal for:
          </p>
          <ul
            className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3"
            data-testid="beta-audience-list"
          >
            {AUDIENCE.map((line, i) => (
              <li
                key={line}
                data-testid={`beta-audience-item-${i}`}
                className="flex items-start gap-2.5 text-sm md:text-base"
              >
                <span
                  className="mt-1 inline-flex items-center justify-center w-4 h-4 rounded-full flex-shrink-0"
                  style={{
                    background: "color-mix(in srgb, var(--v2-accent) 22%, transparent)",
                    color: "var(--v2-accent)",
                  }}
                >
                  <Check className="w-2.5 h-2.5" />
                </span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Section 4 — What you'll be able to test */}
      <section
        data-testid="beta-features"
        className="relative border-t"
        style={{ borderColor: "var(--v2-border)" }}
      >
        <div className="max-w-4xl mx-auto px-5 md:px-10 py-14 md:py-20">
          <div
            className="text-[10px] uppercase tracking-[0.22em]"
            style={{ color: "var(--v2-accent)" }}
          >
            What's Included
          </div>
          <h2 className="mt-2 font-semibold tracking-tight text-[24px] md:text-[30px]">
            What you'll be able to test
          </h2>
          <ul
            className="mt-6 space-y-3"
            data-testid="beta-features-list"
          >
            {BETA_FEATURES.map((line, i) => (
              <li
                key={line}
                data-testid={`beta-features-item-${i}`}
                className="flex items-start gap-3 text-sm md:text-base"
              >
                <Target
                  className="w-4 h-4 mt-1 flex-shrink-0"
                  style={{ color: "var(--v2-accent)" }}
                  aria-hidden="true"
                />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Section 5 — Form */}
      <section
        data-testid="beta-form-section"
        ref={formRef}
        className="relative border-t"
        style={{
          borderColor: "var(--v2-border)",
          background: "var(--v2-surface-subtle)",
        }}
      >
        <div className="max-w-3xl mx-auto px-5 md:px-10 py-14 md:py-20">
          <div
            className="text-[10px] uppercase tracking-[0.22em]"
            style={{ color: "var(--v2-accent)" }}
          >
            Request Access
          </div>
          <h2 className="mt-2 font-semibold tracking-tight text-[24px] md:text-[30px]">
            Join the beta
          </h2>
          <p className="mt-2 text-sm md:text-base" style={{ color: "var(--v2-text-muted)" }}>
            Tell us a bit about yourself and we'll reach out with next steps.
          </p>

          {confirmation ? (
            <div
              data-testid="beta-form-confirmation"
              role="status"
              aria-live="polite"
              className="mt-8 rounded-2xl border p-6 md:p-7"
              style={{
                borderColor: "var(--v2-border)",
                background: "var(--v2-surface)",
              }}
            >
              <div className="flex items-start gap-3">
                <div
                  className="inline-flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0"
                  style={{
                    background: "color-mix(in srgb, var(--v2-accent) 22%, transparent)",
                    color: "var(--v2-accent)",
                  }}
                >
                  <Check className="w-4 h-4" />
                </div>
                <div>
                  <h3
                    className="font-semibold text-base md:text-lg tracking-tight"
                    data-testid="beta-form-confirmation-title"
                  >
                    Request received
                  </h3>
                  <p
                    className="mt-1.5 text-sm md:text-base"
                    style={{ color: "var(--v2-text-muted)" }}
                    data-testid="beta-form-confirmation-body"
                  >
                    {confirmation.message}
                  </p>
                  {confirmation.beta_code_instructions ? (
                    <p
                      className="mt-3 text-sm rounded-md p-3 border"
                      style={{
                        color: "var(--v2-text)",
                        borderColor: "var(--v2-border)",
                        background: "var(--v2-surface-subtle)",
                      }}
                      data-testid="beta-form-confirmation-instructions"
                    >
                      {confirmation.beta_code_instructions}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="beta-form"
              noValidate
              className="mt-8 space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                  label="First name"
                  name="first_name"
                  value={form.first_name}
                  onChange={(v) => setField("first_name", v)}
                  required
                  error={fieldErrors.first_name}
                  testId="beta-form-first-name"
                  autoComplete="given-name"
                />
                <Field
                  label="Last name"
                  name="last_name"
                  value={form.last_name}
                  onChange={(v) => setField("last_name", v)}
                  required
                  error={fieldErrors.last_name}
                  testId="beta-form-last-name"
                  autoComplete="family-name"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={(v) => setField("email", v)}
                  required
                  error={fieldErrors.email}
                  testId="beta-form-email"
                  autoComplete="email"
                  inputMode="email"
                />
                <Field
                  label="Phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={(v) => setField("phone", v)}
                  required
                  error={fieldErrors.phone}
                  testId="beta-form-phone"
                  autoComplete="tel"
                  inputMode="tel"
                  placeholder="(555) 555-0123"
                />
              </div>

              <fieldset
                data-testid="beta-form-interest-group"
                className="rounded-xl border p-4"
                style={{ borderColor: fieldErrors.interest_area ? "var(--v2-danger, #dc2626)" : "var(--v2-border)", background: "var(--v2-surface)" }}
              >
                <legend
                  className="text-xs uppercase tracking-[0.18em] px-1"
                  style={{ color: "var(--v2-text-muted)" }}
                >
                  Interest area *
                </legend>
                <div className="mt-2 flex flex-wrap gap-2">
                  {INTEREST_OPTIONS.map((opt) => {
                    const active = form.interest_area === opt.value;
                    return (
                      <label
                        key={opt.value}
                        data-testid={`beta-form-interest-${opt.value}`}
                        className="cursor-pointer rounded-full border px-3.5 py-1.5 text-sm transition-colors"
                        style={{
                          borderColor: active ? "var(--v2-accent)" : "var(--v2-border)",
                          background: active
                            ? "color-mix(in srgb, var(--v2-accent) 14%, transparent)"
                            : "var(--v2-surface)",
                          color: active ? "var(--v2-accent)" : "var(--v2-text)",
                        }}
                      >
                        <input
                          type="radio"
                          name="interest_area"
                          value={opt.value}
                          checked={active}
                          onChange={() => setField("interest_area", opt.value)}
                          className="sr-only"
                        />
                        {opt.label}
                      </label>
                    );
                  })}
                </div>
                {fieldErrors.interest_area ? (
                  <p
                    data-testid="beta-form-interest-error"
                    className="mt-2 text-xs"
                    style={{ color: "var(--v2-danger, #dc2626)" }}
                  >
                    {fieldErrors.interest_area}
                  </p>
                ) : null}
              </fieldset>

              <fieldset
                data-testid="beta-form-client-group"
                className="rounded-xl border p-4"
                style={{ borderColor: fieldErrors.is_current_client ? "var(--v2-danger, #dc2626)" : "var(--v2-border)", background: "var(--v2-surface)" }}
              >
                <legend
                  className="text-xs uppercase tracking-[0.18em] px-1"
                  style={{ color: "var(--v2-text-muted)" }}
                >
                  Are you a current client? *
                </legend>
                <div className="mt-2 flex gap-2">
                  {[
                    { value: "yes", label: "Yes" },
                    { value: "no", label: "No" },
                  ].map((opt) => {
                    const active = form.is_current_client === opt.value;
                    return (
                      <label
                        key={opt.value}
                        data-testid={`beta-form-client-${opt.value}`}
                        className="cursor-pointer rounded-full border px-4 py-1.5 text-sm transition-colors"
                        style={{
                          borderColor: active ? "var(--v2-accent)" : "var(--v2-border)",
                          background: active
                            ? "color-mix(in srgb, var(--v2-accent) 14%, transparent)"
                            : "var(--v2-surface)",
                          color: active ? "var(--v2-accent)" : "var(--v2-text)",
                        }}
                      >
                        <input
                          type="radio"
                          name="is_current_client"
                          value={opt.value}
                          checked={active}
                          onChange={() => setField("is_current_client", opt.value)}
                          className="sr-only"
                        />
                        {opt.label}
                      </label>
                    );
                  })}
                </div>
                {fieldErrors.is_current_client ? (
                  <p
                    data-testid="beta-form-client-error"
                    className="mt-2 text-xs"
                    style={{ color: "var(--v2-danger, #dc2626)" }}
                  >
                    {fieldErrors.is_current_client}
                  </p>
                ) : null}
              </fieldset>

              <div>
                <label
                  className="text-xs uppercase tracking-[0.18em] block"
                  style={{ color: "var(--v2-text-muted)" }}
                  htmlFor="beta-form-message"
                >
                  Message (optional)
                </label>
                <textarea
                  id="beta-form-message"
                  data-testid="beta-form-message"
                  value={form.message}
                  onChange={(e) => setField("message", e.target.value.slice(0, 500))}
                  maxLength={500}
                  rows={4}
                  className="mt-1.5 w-full rounded-xl border px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-offset-0 transition-shadow"
                  style={{
                    borderColor: "var(--v2-border)",
                    background: "var(--v2-surface)",
                    color: "var(--v2-text)",
                  }}
                />
                <div
                  className="mt-1 text-xs text-right"
                  style={{ color: "var(--v2-text-muted)" }}
                  data-testid="beta-form-message-remaining"
                >
                  {remaining} characters remaining
                </div>
                {fieldErrors.message ? (
                  <p
                    className="mt-1 text-xs"
                    style={{ color: "var(--v2-danger, #dc2626)" }}
                  >
                    {fieldErrors.message}
                  </p>
                ) : null}
              </div>

              {error ? (
                <div
                  data-testid="beta-form-error"
                  role="alert"
                  className="rounded-xl border px-4 py-3 text-sm"
                  style={{
                    borderColor: "var(--v2-danger, #dc2626)",
                    color: "var(--v2-danger, #dc2626)",
                    background: "color-mix(in srgb, var(--v2-danger, #dc2626) 8%, transparent)",
                  }}
                >
                  {error}
                </div>
              ) : null}

              <div className="flex items-center justify-between gap-4 flex-wrap">
                <p
                  className="text-xs max-w-md"
                  style={{ color: "var(--v2-text-muted)" }}
                >
                  We'll only use your info to invite you to the beta. No spam.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="beta-form-submit"
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-all disabled:opacity-60 disabled:cursor-not-allowed hover:translate-y-[-1px]"
                  style={{
                    background: "var(--v2-accent)",
                    color: "#fff",
                    boxShadow:
                      "0 10px 24px -8px color-mix(in srgb, var(--v2-accent) 55%, transparent)",
                  }}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      Request Beta Access <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Section 6 — Trust & Disclaimer */}
      <section
        data-testid="beta-disclaimer"
        className="relative border-t"
        style={{ borderColor: "var(--v2-border)" }}
      >
        <div className="max-w-4xl mx-auto px-5 md:px-10 py-14 md:py-16">
          <div
            className="rounded-2xl border p-5 md:p-6 flex items-start gap-4"
            style={{ borderColor: "var(--v2-border)", background: "var(--v2-surface)" }}
          >
            <ShieldCheck
              className="w-6 h-6 flex-shrink-0 mt-0.5"
              style={{ color: "var(--v2-accent)" }}
              aria-hidden="true"
            />
            <div>
              <h3 className="font-semibold text-base md:text-lg tracking-tight">
                Educational, not a guarantee
              </h3>
              <p
                className="mt-2 text-sm md:text-base leading-relaxed"
                style={{ color: "var(--v2-text-muted)" }}
              >
                {DISCLAIMER_TEXT}
              </p>
              {/* Dual-brand attribution — product name vs. legal
                  entity. Renders immediately below the compliance
                  disclaimer, same tone/size, so both compliance-
                  adjacent notes read together. */}
              <p
                data-testid="beta-footer-legal-attribution"
                className="mt-3 text-xs md:text-sm leading-relaxed"
                style={{ color: "var(--v2-text-muted)" }}
              >
                Informed OS™ is a product of Informed Accelerator, Inc.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 7 — Footer */}
      <footer
        data-testid="beta-footer"
        className="border-t"
        style={{ borderColor: "var(--v2-border)" }}
      >
        <div className="max-w-6xl mx-auto px-5 md:px-10 py-8 md:py-10 flex flex-wrap items-center justify-between gap-4 text-sm">
          <div
            className="flex items-center gap-4 flex-wrap"
            style={{ color: "var(--v2-text-muted)" }}
          >
            <span>© {new Date().getFullYear()} {APP_NAME}</span>
            <Link to="/legal/terms" data-testid="beta-footer-terms" className="hover:underline">
              Terms
            </Link>
            <Link to="/legal/privacy" data-testid="beta-footer-privacy" className="hover:underline">
              Privacy
            </Link>
            <a
              href="mailto:support@informedaccelerator.com"
              data-testid="beta-footer-support"
              className="hover:underline"
            >
              support@informedaccelerator.com
            </a>
          </div>
          <Link
            to="/login"
            data-testid="beta-footer-signin"
            className="hover:underline"
            style={{ color: "var(--v2-text)" }}
          >
            Sign In
          </Link>
        </div>
      </footer>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Field — thin V2-styled text input primitive local to this page. Keeps the
// beta landing self-contained without pulling in a design-system dependency.
// ---------------------------------------------------------------------------
function Field({
  label,
  name,
  value,
  onChange,
  required,
  type = "text",
  error,
  testId,
  autoComplete,
  inputMode,
  placeholder,
}) {
  return (
    <label className="block">
      <span
        className="text-xs uppercase tracking-[0.18em] block"
        style={{ color: "var(--v2-text-muted)" }}
      >
        {label}
        {required ? " *" : ""}
      </span>
      <input
        type={type}
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testId}
        autoComplete={autoComplete}
        inputMode={inputMode}
        placeholder={placeholder}
        className="mt-1.5 w-full rounded-xl border px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-offset-0 transition-shadow"
        style={{
          borderColor: error ? "var(--v2-danger, #dc2626)" : "var(--v2-border)",
          background: "var(--v2-surface)",
          color: "var(--v2-text)",
        }}
      />
      {error ? (
        <span
          data-testid={testId ? `${testId}-error` : undefined}
          className="mt-1 block text-xs"
          style={{ color: "var(--v2-danger, #dc2626)" }}
        >
          {error}
        </span>
      ) : null}
    </label>
  );
}
